import { OBS_WORKER_URL } from '../config.js';
import { storage } from '../utils/storage.js';
import { resolveImageGenError } from '../translations.js';

const FEATURE_FLAGS = new Set([
  'sprite_dragged',
  'sprite_zoomed',
  'voice_used',
  'catalog_load_more',
  'rankings_filter_used',
  'gender_toggled',
  'response_style_selected',
  'hangout_tip_seen'
]);

const SESSION_KEY = 'analytics_session_id';
const FLAGS_KEY = 'analytics_flags_sent';

const readSentFlags = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(FLAGS_KEY) || '[]');
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const writeSentFlags = (flags) => {
  sessionStorage.setItem(FLAGS_KEY, JSON.stringify([...flags]));
};

class HangoutAnalyticsTracker {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.sessionStarted = false;
  }

  getOrCreateSessionId() {
    try {
      const existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) return existing;
      const created = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, created);
      return created;
    } catch {
      return crypto.randomUUID();
    }
  }

  async trackSessionStart() {
    if (this.sessionStarted) return;
    this.sessionStarted = true;
    const ok = await this.send('session_start', {});
    if (!ok) this.sessionStarted = false;
  }

  trackFeature(flag) {
    if (!FEATURE_FLAGS.has(flag)) return;
    const sent = readSentFlags();
    if (sent.has(flag)) return;
    sent.add(flag);
    writeSentFlags(sent);
    this.send('feature_flag', { flag }).then((ok) => {
      if (ok) return;
      const current = readSentFlags();
      current.delete(flag);
      writeSentFlags(current);
    });
  }

  trackEvent(eventType, eventData = {}) {
    if (eventType !== 'js_error') return;
    this.send('js_error', eventData);
  }

  async send(eventType, eventData = {}) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const userToken = storage.getToken();
      if (userToken) headers.Authorization = `Bearer ${userToken}`;

      const response = await fetch(`${OBS_WORKER_URL}/read-event`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          event_type: eventType,
          event_data: {
            session_id: this.sessionId,
            ...eventData
          }
        })
      });

      if (!response.ok) {
        console.warn('Analytics tracking failed:', response.status);
        return false;
      }
      return true;
    } catch (error) {
      console.warn('Analytics tracking error:', error);
      return false;
    }
  }
}

let analyticsTracker = null;
let errorsInstalled = false;

export function getAnalyticsTracker() {
  if (!analyticsTracker) analyticsTracker = new HangoutAnalyticsTracker();
  return analyticsTracker;
}

export function initializeHangoutAnalytics() {
  if (errorsInstalled) return;
  errorsInstalled = true;
  installFrontendErrorTracking(getAnalyticsTracker());
}

const IMAGE_GEN_WORKER_ERROR_CODES = [
  'quota_exhausted',
  'prompt_rejected',
  'prompt_rejected_child_explicit',
  'image_generation_api_error',
];
const IMAGE_GEN_WORKER_EXACT_MESSAGES = [
  'Daily image generation limit reached. Try again tomorrow or after creating a character/scenario.',
  'Slow path unavailable',
  'Image generation API error',
  'SINKIN_API_TOKEN not configured',
];
const IMAGE_GEN_WORKER_MESSAGE_PREFIXES = [
  'image-producer submission failed (',
  'slow path not configured: image_producer service binding missing',
  'runware api failed with status ',
  'runware video submit failed with status ',
  'runware poll failed with status ',
  'video generation failed: ',
  'video generation timed out after ',
  'non-json response received',
  'failed to parse sinkin response: ',
  'sinkin api error',
  'sinkin fallback api error: ',
  'sinkin fallback api error for initial image',
  'no image received from runware',
  'no initial image received from sinkin',
  'no images received from sinkin',
  'no videourl in completed task',
  'no video received from runware vidu',
  'no video received from runware kling',
  'runware_api_key not configured for vidu model',
  'runware_api_key not configured for kling model',
];

const installFrontendErrorTracking = (tracker) => {
  let errorCount = 0;
  const MAX_ERRORS_PER_SESSION = 5;
  let cachedImageGenWorkerMessages = null;

  const truncate = (value, maxLength) => String(value || '').substring(0, maxLength);
  const normalizeErrorMessage = (value) => String(value || '')
    .trim()
    .replace(/^uncaught(?:\s*\(in promise\))?:?\s*/i, '')
    .replace(/^error:\s*/i, '')
    .trim();
  const sanitizeSensitiveText = (value, maxLength) => {
    const text = truncate(value, maxLength);
    if (!text) return null;
    return text
      .replace(/([?&](?:token|access_token|refresh_token|password|passwd|secret|key)=)[^&\s]+/gi, '$1[redacted]')
      .replace(/(authorization:\s*bearer\s+)[^\s]+/gi, '$1[redacted]');
  };
  const getImageGenWorkerMessages = () => {
    if (cachedImageGenWorkerMessages) return cachedImageGenWorkerMessages;
    const exactMessages = new Set(
      [...IMAGE_GEN_WORKER_ERROR_CODES, ...IMAGE_GEN_WORKER_EXACT_MESSAGES]
        .map(normalizeErrorMessage)
        .map((message) => message.toLowerCase())
        .filter(Boolean)
    );
    IMAGE_GEN_WORKER_ERROR_CODES.forEach((code) => {
      const translated = normalizeErrorMessage(resolveImageGenError(code));
      if (translated) exactMessages.add(translated.toLowerCase());
    });
    cachedImageGenWorkerMessages = exactMessages;
    return cachedImageGenWorkerMessages;
  };
  const isWorkerLoggedImageGenError = (message) => {
    const normalized = normalizeErrorMessage(message);
    if (!normalized) return false;
    const lower = normalized.toLowerCase();
    if (getImageGenWorkerMessages().has(lower)) return true;
    return IMAGE_GEN_WORKER_MESSAGE_PREFIXES.some((prefix) => lower.startsWith(prefix));
  };
  const getReleaseId = () => {
    const releaseFromWindow = typeof window.__APP_RELEASE__ === 'string' ? window.__APP_RELEASE__ : null;
    if (releaseFromWindow) return truncate(releaseFromWindow, 120);
    const releaseMeta = document.querySelector('meta[name="app-release"]')?.getAttribute('content');
    return releaseMeta ? truncate(releaseMeta, 120) : null;
  };
  const parseFrameFromStack = (stack) => {
    if (!stack) return { stackTopFrame: null, sourceFromStack: null, lineFromStack: null, colFromStack: null };
    const frameLine = String(stack)
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.startsWith('at ') || line.includes('.js:') || line.includes('http'));
    if (!frameLine) return { stackTopFrame: null, sourceFromStack: null, lineFromStack: null, colFromStack: null };
    const locationMatch = frameLine.match(/((?:https?:\/\/|\/)[^\s)]+):(\d+):(\d+)/);
    if (!locationMatch) return { stackTopFrame: truncate(frameLine, 240), sourceFromStack: null, lineFromStack: null, colFromStack: null };
    return {
      stackTopFrame: truncate(frameLine, 240),
      sourceFromStack: truncate(locationMatch[1], 180),
      lineFromStack: Number.parseInt(locationMatch[2], 10) || null,
      colFromStack: Number.parseInt(locationMatch[3], 10) || null
    };
  };
  const normalizeErrorPayload = ({ message, source, line, col = null, stack = null, errorName = null, isUnhandledRejection = false }) => {
    const cleanMessage = sanitizeSensitiveText(message, 400) || '(no message)';
    const cleanStack = sanitizeSensitiveText(stack, 2000);
    const frame = parseFrameFromStack(cleanStack);
    const resolvedSource = truncate(source || frame.sourceFromStack || '', 180);
    const resolvedLine = line ?? frame.lineFromStack ?? null;
    const resolvedCol = col ?? frame.colFromStack ?? null;
    const isScriptError = cleanMessage === 'Script error.' || resolvedLine === 0;
    return {
      message: truncate(cleanMessage, 200),
      source: truncate(resolvedSource, 100),
      line: resolvedLine,
      error_name: truncate(errorName || null, 120),
      stack: cleanStack,
      stack_top_frame: frame.stackTopFrame,
      col: resolvedCol,
      url_path: truncate(window.location.pathname || '', 180),
      is_unhandled_rejection: Boolean(isUnhandledRejection),
      is_cross_origin_script_error: Boolean(isScriptError),
      runtime_context: truncate(document.body?.dataset?.runtimeContext || window.location.pathname || 'unknown', 120),
      release_id: getReleaseId()
    };
  };
  const sendError = (payload) => {
    if (errorCount >= MAX_ERRORS_PER_SESSION) return;
    if (isWorkerLoggedImageGenError(payload?.message)) return;
    errorCount++;
    tracker.trackEvent('js_error', payload);
  };

  window.addEventListener('error', (event) => {
    sendError(normalizeErrorPayload({
      message: event.message,
      source: event.filename,
      line: event.lineno,
      col: event.colno ?? null,
      stack: event.error?.stack ?? null,
      errorName: event.error?.name ?? null,
      isUnhandledRejection: false
    }));
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    sendError(normalizeErrorPayload({
      message: reason?.message ?? String(reason),
      source: null,
      line: null,
      col: null,
      stack: reason?.stack ?? null,
      errorName: reason?.name ?? (reason?.constructor?.name || 'UnhandledRejection'),
      isUnhandledRejection: true
    }));
  });
};
